const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

const MAX_UNESCAPE_PASSES = 3;

const hasHtmlTags = (value: string) => /<[a-z][^>]*>/i.test(value);

const hasEscapedTags = (value: string) => /&(?:amp;)*lt;/i.test(value);

/**
 * Decode HTML entities (named + numeric) in a string. Single pass — does not
 * recursively unwrap double-escaped content; use unescapeHtmlMarkup for that.
 */
export const decodeHtmlEntities = (value: string): string =>
  value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    const lower = entity.toLowerCase();
    if (lower.startsWith("#")) {
      const code = lower.startsWith("#x")
        ? Number.parseInt(lower.slice(2), 16)
        : Number.parseInt(lower.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return match;
      return String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[lower] ?? match;
  });

/**
 * Some job feeds deliver HTML descriptions entity-escaped (sometimes doubly,
 * e.g. "&amp;lt;h1&amp;gt;"), which would otherwise render as literal tag text.
 * Decodes only while the string looks like escaped markup with no real tags,
 * so plain text containing stray entities is left untouched.
 */
export const unescapeHtmlMarkup = (value: string): string => {
  let current = value;
  for (let pass = 0; pass < MAX_UNESCAPE_PASSES; pass += 1) {
    if (hasHtmlTags(current) || !hasEscapedTags(current)) break;
    const decoded = decodeHtmlEntities(current);
    if (decoded === current) break;
    current = decoded;
  }
  return current;
};
